import React, { useEffect, useState } from 'react';
import { useAuth } from './context/AuthContext';
import { resolveScraperOrigin } from './utils/scraperUrl';
import ThemeToggle from './components/ThemeToggle';

// LoginGate screens, all rendered in the same card:
//   'login'   – email + password (Supabase sign-in)
//   'signup'  – email + username + password + confirm (Supabase sign-up)
//   'forgot'  – email-only form; Supabase emails a password-reset link
//   'reset'   – set a new password (shown after the user follows that link)
// Auth is delegated to Supabase (shared with the marketplace app); the local
// backend only verifies the token and tracks role + approval.
function LoginGate({ children }) {
  const {
    isAuthenticated,
    pendingApproval,
    recoveryMode,
    login,
    signup,
    requestPasswordReset,
    updatePassword,
    logout,
    loading,
    error,
  } = useAuth();

  const [redirectingToNext, setRedirectingToNext] = useState(false);
  const scraperOrigin = React.useMemo(
    () => resolveScraperOrigin(process.env.REACT_APP_SCRAPER_ORIGIN),
    [],
  );

  const getSafeNextUrl = React.useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const rawNext = params.get('next');
    if (!rawNext) return null;

    try {
      const target = new URL(rawNext, window.location.origin);
      const allowedOrigins = new Set([window.location.origin, scraperOrigin]);
      if (!allowedOrigins.has(target.origin)) {
        return null;
      }
      return target;
    } catch {
      return null;
    }
  }, [scraperOrigin]);

  const redirectToNextIfPresent = React.useCallback(() => {
    const target = getSafeNextUrl();
    if (!target) return false;

    if (target.origin === scraperOrigin) {
      // Hand the Supabase access token to the scraper app so it can call the
      // shared backend. The scraper verifies the same Supabase JWT.
      const token = localStorage.getItem('auth_token');
      const user = localStorage.getItem('auth_user');
      if (token) {
        target.searchParams.set('token', token);
      }
      if (user) {
        target.searchParams.set('user', user);
      }
    }

    setRedirectingToNext(true);
    window.location.replace(target.toString());
    return true;
  }, [getSafeNextUrl, scraperOrigin]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('logout') === 'true') {
      logout();
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [logout]);

  useEffect(() => {
    // During password recovery Supabase has established a (temporary) session,
    // so isAuthenticated is true — but we must stay on the reset form rather
    // than bouncing to ?next=.
    if (!isAuthenticated || recoveryMode) return;
    redirectToNextIfPresent();
  }, [isAuthenticated, recoveryMode, redirectToNextIfPresent]);

  // Seed from recoveryMode (itself seeded synchronously from the URL) so the
  // reset form renders on the first paint — no flash of the login fields.
  const [mode, setMode] = useState(recoveryMode ? 'reset' : 'login');

  // Safety net for recovery sessions detected asynchronously (event arrives
  // after the URL hash was already consumed): still jump to the reset form.
  useEffect(() => {
    if (recoveryMode) setMode('reset');
  }, [recoveryMode]);

  // Shared fields
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [localError, setLocalError] = useState('');
  const [notice, setNotice] = useState('');

  const clearMessages = () => {
    setLocalError('');
    setNotice('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    const ok = await login(email.trim().toLowerCase(), password);
    if (ok) {
      redirectToNextIfPresent();
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    clearMessages();
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    const result = await signup({
      email: email.trim().toLowerCase(),
      username: username.trim(),
      password,
    });
    if (result && result.ok) {
      setMode('login');
      setPassword('');
      setConfirmPassword('');
      setNotice(
        'Account created. Confirm your email if prompted, then wait for an '
        + 'administrator to approve your account before logging in.'
      );
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    clearMessages();
    const result = await requestPasswordReset(email.trim().toLowerCase());
    if (result && result.ok) {
      setMode('login');
      setNotice(
        'If an account exists for that email, a password-reset link has been '
        + 'sent. Follow the link to choose a new password.'
      );
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    clearMessages();
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    const result = await updatePassword({ password });
    if (result && result.ok) {
      setMode('login');
      setPassword('');
      setConfirmPassword('');
      setNotice('Password updated. You can now log in with your new password.');
    } else {
      setLocalError(result?.error || 'Could not update password.');
    }
  };

  const switchMode = (next) => {
    setMode(next);
    clearMessages();
    setPassword('');
    setConfirmPassword('');
  };

  const hasNextTarget = Boolean(getSafeNextUrl());

  // A recovery session counts as authenticated, but we must keep rendering the
  // reset form so the user can choose a new password instead of being dropped
  // straight into the app (which silently spends the single-use recovery link).
  if (isAuthenticated && !recoveryMode) {
    if (redirectingToNext || hasNextTarget) {
      return null;
    }
    return children;
  }

  const displayError = localError || error;

  const titles = {
    login: 'Log in',
    signup: 'Create your account',
    forgot: 'Reset your password',
    reset: 'Choose a new password',
  };
  const submitLabels = {
    login: loading ? 'Signing in…' : 'Log In',
    signup: loading ? 'Creating account…' : 'Create account',
    forgot: loading ? 'Sending…' : 'Send reset link',
    reset: loading ? 'Updating…' : 'Set new password',
  };
  const handlers = {
    login: handleLogin,
    signup: handleSignup,
    forgot: handleForgot,
    reset: handleReset,
  };

  return (
    <div className="relative min-h-screen bg-canvas flex items-center justify-center">
      <ThemeToggle className="absolute top-4 right-4" />
      <div className="bg-surface border border-hairline rounded-lg shadow-md p-8 w-full max-w-sm">
        <h2 className="font-heading text-2xl font-bold text-center text-brand mb-2">Auction.ai</h2>
        <p className="text-center text-sm text-muted mb-6">{titles[mode]}</p>

        {pendingApproval && (
          <p className="text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-3 text-sm mb-4">
            Your account is awaiting administrator approval.
          </p>
        )}

        <form onSubmit={handlers[mode]} className="space-y-4">
          {(mode === 'login' || mode === 'signup' || mode === 'forgot') && (
            <div>
              <label className="block text-sm font-medium text-ink mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-surface text-ink border border-hairline rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                autoComplete="email"
                autoFocus
                required
              />
            </div>
          )}

          {mode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-ink mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-surface text-ink border border-hairline rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                autoComplete="username"
                required
              />
            </div>
          )}

          {(mode === 'login' || mode === 'signup' || mode === 'reset') && (
            <div>
              <label className="block text-sm font-medium text-ink mb-1">
                {mode === 'reset' ? 'New password' : 'Password'}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-surface text-ink border border-hairline rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
              />
            </div>
          )}

          {(mode === 'signup' || mode === 'reset') && (
            <div>
              <label className="block text-sm font-medium text-ink mb-1">Confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-surface text-ink border border-hairline rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                autoComplete="new-password"
                required
              />
            </div>
          )}

          {notice && <p className="text-positive text-sm">{notice}</p>}
          {displayError && <p className="text-red-500 dark:text-red-400 text-sm">{displayError}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand text-white py-2 rounded-md hover:bg-brand-hover transition disabled:opacity-50"
          >
            {submitLabels[mode]}
          </button>
        </form>

        <div className="mt-4 text-center text-sm text-muted space-y-1">
          {mode === 'login' && (
            <>
              <div>
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="text-brand hover:underline font-medium"
                >
                  Sign up
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="text-brand hover:underline font-medium"
                >
                  Forgot password?
                </button>
              </div>
            </>
          )}
          {mode === 'signup' && (
            <div>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-brand hover:underline font-medium"
              >
                Log in
              </button>
            </div>
          )}
          {(mode === 'forgot' || mode === 'reset') && (
            <div>
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-brand hover:underline font-medium"
              >
                Back to log in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginGate;
